import * as React from "react";
import classNames from "classnames";
import * as styles from "./InputOverride.module.scss";

/**
 * An Input is a single line text input.
 */
export default class Input extends React.Component<InputProps, InputState> {
  handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.setState({ value: event.currentTarget.value });
    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  constructor(props: InputProps) {
    super(props);
    this.state = {
      value: props.value,
    };
  }

  componentWillReceiveProps(newProps: InputProps) {
    if (newProps.value !== this.state.value) {
      this.setState({ value: newProps.value });
    }
  }

  render() {
    const {
      autoFocus,
      disabled,
      name,
      placeholder,
      size,
      type = "text",
      style,
      onBlur,
      onKeyPress,
      autoComplete,
    } = this.props;
    const wrapped = isWrapped(this.props);
    const className = classNames(
      "mtr-form__label",
      getSizeClass(size),
      !wrapped && this.props.className
    );

    const input = (
      <input
        className={className}
        type={type}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        name={name}
        placeholder={placeholder}
        value={this.state.value || ""}
        disabled={disabled}
        onChange={this.handleChange}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        style={wrapped ? undefined : style}
      />
    );

    return addLabel(addSubtext(input, this.props), this.props);
  }
}

export interface InputProps extends ControlProps {
  /** If true, element will request focus when component is rendered */
  autoFocus?: boolean;

  /** Determines whether autocomplete should be allowed for the input */
  autoComplete?: "on" | "off";

  /** The type of the input; defaults to 'text' */
  type?: "text" | "password";

  /** Optional placeholder text */
  placeholder?: string;

  /** Provide an optional size for the input */
  size?: ControlSize;

  /** If true, a label will be next to the input */
  horizontal?: boolean;

  onChange?: (event: React.SyntheticEvent<HTMLInputElement>) => void;

  onBlur?: (event: React.SyntheticEvent<HTMLInputElement>) => void;

  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export interface InputState<T = string> {
  value?: T;
}

export interface DecoratedProps {
  /** An optional right-side subdued label for this control */
  aside?: string;

  /** An optional label for this control */
  label?: string;

  /** Optional helper text for this control */
  subtext?: string | React.ReactNode;
}

export interface ControlProps<T = string> extends DecoratedProps {
  /** A unique name for this control */
  name: string;

  /** Optional additional classes for this control */
  className?: string;

  /** Optional inline styles to apply to a component */
  style?: React.CSSProperties;

  /** The current value of this control */
  value?: T;

  /** If true, control is disabled */
  disabled?: boolean;
}

export type ControlSize = "sm" | "md" | "lg";

export function getSizeClass(size?: ControlSize) {
  if (!size) {
    return;
  }

  switch (size) {
    case "sm":
      return styles.mtrFormControlSm;
    case "md":
      return styles.mtrFormControlMd;
    default:
      return styles.mtrFormControlLg;
  }
}

export function addSubtext<T extends SubtextOptions>(
  component: React.ReactElement<T>,
  options = {} as T
): React.ReactElement<HTMLDivElement> | React.ReactElement<T> {
  const { subtext } = options;
  if (subtext == null) {
    return component;
  }

  const { style } = options;
  const isOuter = options.label == null && options.aside == null;
  const className = classNames(
    styles.subdued,
    styles.subtext,
    isOuter && options.className
  );

  return (
    <div className={styles.mtrFormField} style={isOuter ? style : undefined}>
      {component}
      {subtext ? <p className={className}>{subtext}</p> : undefined}
    </div>
  );
}

export interface SubtextOptions {
  /** The subtext to add */
  subtext?: string | React.ReactNode;

  /** Optional className to add to component */
  className?: string;

  /** Optional inline styles to apply to a component */
  style?: React.CSSProperties;

  /**
   * The label that will be added to this component. This is used to decide
   * whether to apply the className and style.
   */
  label?: string;

  /**
   * The aside that will be added to this component. This is used to decide
   * whether to apply the className and style.
   */
  aside?: string;
}

export function addLabel<T extends LabelOptions>(
  component: React.ReactElement<T>,
  options = {} as T
): React.ReactElement<T> | React.ReactElement<HTMLLabelElement> {
  if (options.label == null && options.aside == null) {
    return component;
  }

  const { checkbox, style } = options;

  const className = classNames(styles.mtrFormLabel, options.className, {
    [styles.mtrFormLabelHorizontal]: options.horizontal || checkbox,
    [styles.mtrFormLabelCheckbox]: checkbox,
    [styles.mtrFormLabelInline]: options.inline,
  });

  return (
    <label className={className} style={style}>
      {checkbox
        ? renderLabeledCheckbox(component, options)
        : renderLabeledControl(component, options)}
    </label>
  );
}

export interface LabelOptions {
  /** The label to add */
  label?: string;

  /** If true, render label beside component */
  horizontal?: boolean;

  /** If true, use checkbox-style labeling */
  checkbox?: boolean;

  /** If aside has a value and horizontal is not set, s */
  aside?: string;

  /** Optional className to add to component */
  className?: string;

  /** Optional inline styles to apply to a component */
  style?: React.CSSProperties;

  /** If true, display as an inline-block element */
  inline?: boolean;
}

/**
 * Indicate whether a given component will be wrapped by a label and/or subtext
 */
// tslint:disable-next-line:no-any
export function isWrapped(
  props: Pick<ControlProps, "label" | "aside" | "subtext">
) {
  const { label, aside, subtext } = props;
  return label != null || aside != null || subtext != null;
}

export function ToggledInput<
  T extends string | string[] | number | undefined = string
>(props: ToggledInputProps<T>) {
  const {
    type,
    checked,
    defaultChecked,
    value,
    disabled,
    name,
    onChange,
    mainClass,
    backdropClass,
    label,
    style,
  } = props;

  const className = classNames(mainClass, label == null && props.className);

  return addLabel(
    <div className={className} style={label == null ? style : undefined}>
      <input
        type={type}
        name={name}
        value={value}
        defaultChecked={defaultChecked}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <div className={backdropClass} />
    </div>,
    { ...props, checkbox: true }
  );
}

export interface ToggledInputProps<T = string>
  extends Omit<ControlProps<T>, "aside" | "subtext"> {
  /**
   * Type of input
   */
  type: "checkbox" | "radio";

  /**
   * A name for this radio button; should be identical for grouped buttons
   */
  name: string;

  /** If true, this radio will be checked */
  checked?: boolean;

  /** If true, this radio will be initially checked */
  defaultChecked?: boolean;

  /** Called when the checkbox value changes */
  onChange?: (event: React.SyntheticEvent<HTMLInputElement>) => void;

  /**
   * A label to display with the input
   */
  label?: string;

  /**
   * The main class for the component
   */
  mainClass: string;

  /**
   * A class for the component's backdrop
   */
  backdropClass: string;
}

export type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

function renderLabeledCheckbox(
  component: React.ReactNode,
  options: LabelOptions
) {
  const { label } = options;
  return (
    <React.Fragment>
      {component}
      {label ? (
        <span className={styles.mtrFormLabelText}>{label}</span>
      ) : undefined}
    </React.Fragment>
  );
}

function renderLabeledControl(
  component: React.ReactNode,
  options: LabelOptions
) {
  const { label, aside } = options;
  return (
    <React.Fragment>
      {label || aside ? (
        <span className={styles.mtrFormLabelText}>
          {label}
          {aside && !options.horizontal && (
            <span className={styles.mtrFormLabelTextAside}>{aside}</span>
          )}
        </span>
      ) : undefined}
      {component}
    </React.Fragment>
  );
}
